import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader, RequireModel } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import SvgViewer from "@/components/SvgViewer";
import TitleBlock from "@/components/TitleBlock";
import PlanActions from "@/components/PlanActions";
import { Button, Card } from "@/components/ui";

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${active ? "border-sky-500 bg-sky-500 text-white" : "border-border bg-card text-foreground hover:border-sky-400"}`}>
    {children}
  </button>
);

export default function Section() {
  const { lang, user } = useAuth();
  const isAr = lang === "ar";
  const [wm] = useWorkingModel();
  const [dir, setDir] = useState("N-S");
  const [floors, setFloors] = useState(wm?.floors || 1);
  const [wallH, setWallH] = useState(3.2);
  const [parapet, setParapet] = useState(1.0);
  const [excavation, setExcavation] = useState(1.5);
  const [basement, setBasement] = useState(!!wm?.has_basement);
  const [showTB, setShowTB] = useState(true);

  const { data, loading, error } = useDeliverable(
    () => api.editorSection({ model: wm.model, floors, wall_height_m: wallH, slab_system: wm.slab_system, section_direction: dir, parapet_height_m: parapet, excavation_depth_m: excavation, has_basement: basement, basement_depth_m: wm?.basement_depth_m || 3.0, design_code: wm.design_code }),
    [dir, floors, wallH, parapet, excavation, basement, wm?.title, wm?.design_code],
    !!wm?.model
  );

  return (
    <RequireModel>
      {() => (
        <div className="mx-auto max-w-6xl px-4 py-6">
          <PageHeader
            eyebrow={isAr ? "القطاعات" : "Section"}
            title={isAr ? "القطاع الرأسي" : "Vertical section"}
            subtitle={isAr ? "قطاع إنشائي يوضح البلاطات والجدران والحفر والمناسيب وسلاسل القياس." : "Structural section showing slabs, walls, excavation, levels and dimension chains."}
            right={
              <div className="flex gap-2">
                {["N-S", "E-W"].map((d) => (
                  <Button key={d} size="sm" variant={dir === d ? "primary" : "outline"} onClick={() => setDir(d)} data-testid={`section-dir-${d}`}>{d}</Button>
                ))}
              </div>
            }
          />
          <PlanActions
            model={wm.model}
            projectName={wm.title}
            sheet="sections"
            sheetMeta={{ floors, wall_height_m: wallH, parapet_height_m: parapet, section_direction: dir, has_basement: basement }}
            testIdPrefix="section-actions"
          />

          {/* per-plan edit toolbar - live recompute */}
          <Card className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 p-3" data-testid="section-toolbar">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "طوابق:" : "Floors:"}</span>
              {[1, 2, 3, 4, 5].map((f) => <Chip key={f} active={floors === f} onClick={() => setFloors(f)} testid={`section-floors-${f}`}>{f}</Chip>)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "ارتفاع الدور:" : "Wall H:"}</span>
              {[2.7, 3.0, 3.2, 3.6].map((h) => <Chip key={h} active={wallH === h} onClick={() => setWallH(h)} testid={`section-wallh-${h}`}>{h}م</Chip>)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "سور السطح:" : "Parapet:"}</span>
              {[0.6, 1.0, 1.2].map((p) => <Chip key={p} active={parapet === p} onClick={() => setParapet(p)} testid={`section-parapet-${p}`}>{p}م</Chip>)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "عمق الحفر:" : "Excavation:"}</span>
              {[1.0, 1.5, 2.0].map((e) => <Chip key={e} active={excavation === e} onClick={() => setExcavation(e)} testid={`section-exc-${e}`}>{e}م</Chip>)}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "قبو:" : "Basement:"}</span>
              <Chip active={basement} onClick={() => setBasement((v) => !v)} testid="section-basement-toggle">{basement ? (isAr ? "نعم" : "On") : (isAr ? "لا" : "Off")}</Chip>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-muted-foreground">{isAr ? "بلوك العنوان:" : "Title block:"}</span>
              <Chip active={showTB} onClick={() => setShowTB((v) => !v)} testid="toggle-title-block">{showTB ? (isAr ? "ظاهر" : "On") : (isAr ? "مخفي" : "Off")}</Chip>
            </div>
          </Card>

          <DeliverableState loading={loading} error={error} isAr={isAr}>
            {data?.svg ? (
              <>
                <SvgViewer svg={data.svg} />
                <p className="mt-3 text-xs text-muted-foreground">{isAr ? `المسافة: ${data.span_m} م · الطوابق: ${data.floors} · سُمك البلاطة: ${data.slab_thickness_m} م · الارتفاع الكلي: ${data.elevations_m?.total_height} م` : `Span: ${data.span_m} m · floors: ${data.floors} · slab: ${data.slab_thickness_m} m · total height: ${data.elevations_m?.total_height} m`}</p>
                {showTB && (
                  <TitleBlock isAr={isAr} projectName={wm?.title} sheetTitle={isAr ? `قطاع رأسي ${dir}` : `Vertical section ${dir}`} sheetNo="S-10" scale="1:100" designCode={wm?.design_code} firm={user?.firm_profile} />
                )}
              </>
            ) : null}
          </DeliverableState>
        </div>
      )}
    </RequireModel>
  );
}
